/**
 * Input/output contracts for the Audience callable functions (ADR-120).
 *
 * Replaces the prior `event-group-callables.ts`. Operations: createAudience,
 * updateAudience, deleteAudience, bulkCreateAudiences. All four reject
 * writes targeting `system: true` audiences (role audiences and personal
 * audiences are bootstrap-managed only).
 *
 * Anonymous compositions are NOT created via these callables — they're
 * managed by the parent doc's compose callable (createSession,
 * createTravelItem, sendNotification, plus their update/delete paths).
 *
 * Input shapes are Zod schemas with `.strict()` so the wrapper rejects
 * unknown fields at the request boundary; types below are derived via
 * `z.infer`.
 */

import { z } from 'zod';
import { audienceMembersSchema } from '../schemas/audience';
import { roleSchema } from '../schemas/role';

export const createAudienceInputSchema = z
  .object({
    eventId: z.string().min(1),
    /** Required and non-empty — only named audiences are created via this callable. */
    name: z.string().min(1),
    description: z.string().nullable(),
    members: audienceMembersSchema,
  })
  .strict();

export type CreateAudienceInput = z.infer<typeof createAudienceInputSchema>;

export interface CreateAudienceResponse {
  success: true;
  audienceId: string;
}

export const updateAudienceInputSchema = z
  .object({
    audienceId: z.string().min(1),
    /** Fields to update. `members` replaces wholesale (no diff semantics). */
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    members: audienceMembersSchema.optional(),
  })
  .strict();

export type UpdateAudienceInput = z.infer<typeof updateAudienceInputSchema>;

export interface UpdateAudienceResponse {
  success: true;
}

export const deleteAudienceInputSchema = z
  .object({
    audienceId: z.string().min(1),
  })
  .strict();

export type DeleteAudienceInput = z.infer<typeof deleteAudienceInputSchema>;

export interface DeleteAudienceResponse {
  success: true;
  /**
   * Number of consumer docs (sessions + travel items + notifications)
   * that pointed at this audience and have been re-pointed at a fallback
   * empty-audience or had their references cleared. Surfaced so the
   * organizer's confirm dialog can show the blast radius.
   */
  unlinkedConsumerCount: number;
}

/**
 * Bulk-create row contract for named audiences (custom groups).
 *
 * Pre-validated by the admin's `audienceImportSpec` (see
 * `apps/admin/src/utils/imports/audienceImportSpec.ts`). The callable
 * resolves `memberEmails[]` to `{ existingUserIds[], stagedContactIds[] }`
 * server-side; emails that match neither a user nor a contact in this
 * event's roster fail the row with a clear message. `roles[]` is the
 * auto-include-by-role mechanism on Audience.members.
 */
export const bulkCreateAudiencesRowSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    /** Auto-include-by-role: every active eventMembership with one of these roles. */
    roles: z.array(roleSchema),
    /**
     * Direct member emails. Server resolves each to an existing user uid
     * (writes to `members.userIds`) or to a contact id (writes to
     * `contact.stagedAudienceIds` for pre-claim materialization).
     */
    memberEmails: z.array(z.string().email()),
  })
  .strict();

export type BulkCreateAudiencesRow = z.infer<
  typeof bulkCreateAudiencesRowSchema
>;

export const bulkCreateAudiencesInputSchema = z
  .object({
    eventId: z.string().min(1),
    rows: z.array(bulkCreateAudiencesRowSchema).min(1).max(500),
  })
  .strict();

export type BulkCreateAudiencesInput = z.infer<
  typeof bulkCreateAudiencesInputSchema
>;

export type BulkCreateAudiencesRowStatus =
  | 'created'
  | 'duplicate_name' // an existing audience has the same name in this event
  | 'unresolved_emails' // one or more emails didn't match any user or contact
  | 'failed';

export interface BulkCreateAudiencesRowResult {
  index: number;
  name: string;
  status: BulkCreateAudiencesRowStatus;
  /** New audience document ID, populated when status === 'created'. */
  audienceId?: string;
  /** Counts of how each email resolved (only populated when status === 'created'). */
  resolvedUsers?: number;
  resolvedContacts?: number;
  /** Emails that failed to resolve. Populated when status === 'unresolved_emails'. */
  unresolvedEmails?: string[];
  /** Populated when status === 'duplicate_name' or 'failed'. */
  message?: string;
}

export interface BulkCreateAudiencesResponse {
  success: true;
  results: BulkCreateAudiencesRowResult[];
  summary: {
    created: number;
    duplicateName: number;
    unresolvedEmails: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveAudiences — Phase C of the resolve-and-propose architecture.
//
// Same input as bulkCreate (eventId + rows), but the callable does NOT write.
// Returns per-row + per-email proposals so the admin dialog can surface
// ADR-136's "one identity per email globally" invariant before any write:
// "alice@apex.com is a claimed roster person (from Q3 2025 Summit)",
// "bob@summit.com is a pending roster person", "newperson@firm.com is not
// on the event roster". Without this surface, the import would silently dedupe
// by email at write time and the organizer would never see the prior-event
// affiliations.
// ---------------------------------------------------------------------------

export const bulkResolveAudiencesInputSchema = bulkCreateAudiencesInputSchema;

export type BulkResolveAudiencesInput = z.infer<
  typeof bulkResolveAudiencesInputSchema
>;

export type BulkResolveAudiencesRowStatus =
  | 'new'              // name doesn't collide + every email resolves to a user or contact
  | 'matches_existing' // an audience with this name already exists in this event
  | 'partial'          // at least one email is no_match — organizer must edit before write
  | 'failed';          // unexpected per-row server error

/**
 * Per-email proposal flattened into a UI-friendly shape. Keeps the source
 * tag (`existing_user` / `existing_contact` / `no_match`) plus the
 * minimum the review surface needs to render a row card without a second
 * round-trip.
 */
export interface BulkResolveAudiencesEmailProposal {
  /** Original typed email from the workbook row, casing preserved. */
  email: string;
  source: 'existing_user' | 'existing_contact' | 'no_match';
  /** Populated when source === 'existing_user'. */
  userId?: string;
  /** Populated when source === 'existing_contact'. */
  contactId?: string;
  /** Best-effort name for the surfaced person; falls back to email when unnamed. */
  displayName?: string;
  photoURL?: string | null;
  /** Prior-event affiliations for the matched person — surfaced so the organizer can sanity-check. */
  priorEventNames?: string[];
}

export interface BulkResolveAudiencesProposal {
  /**
   * When status === 'matches_existing': the doc ID of the existing
   * audience. UI uses this to show "this row maps to an existing
   * audience X" so the organizer can either skip the row or fix the
   * workbook name.
   */
  matchedExistingAudienceId?: string;
  /**
   * Per-email proposals, in the same order as the workbook row's
   * `memberEmails`. The dialog renders one chip per email so the
   * organizer can confirm or override each match.
   */
  emailProposals: BulkResolveAudiencesEmailProposal[];
  /**
   * Emails that didn't resolve to a user or contact. Surfaced separately
   * for the dialog's "fix and re-import" affordance, even though the
   * same info is reachable via the per-email proposals.
   */
  unmatchedEmails: string[];
}

export interface BulkResolveAudiencesRowResult {
  index: number;
  /** Original typed name from the workbook row. */
  typedName: string;
  status: BulkResolveAudiencesRowStatus;
  /** Populated when status === 'new', 'matches_existing', or 'partial'. */
  proposal?: BulkResolveAudiencesProposal;
  /** Populated for status === 'failed'. */
  message?: string;
}

export interface BulkResolveAudiencesResponse {
  success: true;
  results: BulkResolveAudiencesRowResult[];
  summary: {
    new: number;
    matchesExisting: number;
    partial: number;
    failed: number;
  };
}
